/* eslint-disable react/no-this-in-sfc */
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import SegmentsPieChartTooltip from "components/stocks/modern/chart/SegmentsPieChartTooltip";
import { useMinWidthTailwindBreakpoints } from "components/ui/hooks/useTailwindBreakpoints";
import useTheme from "components/ui/useTheme";
import aeonik from "components/utils/aeonik";
import { renderToStaticMarkup } from "react-dom/server";
import { useIntl } from "react-intl";
import colors from "tailwindcss/colors";

interface SegmentsPieChartProps {
  className?: string;
  currency?: string;
  segments: {
    name: string;
    value: number;
    financialValue: number;
  }[];
}
export default function SegmentsPieChart({ currency, segments, className }: SegmentsPieChartProps) {
  const intl = useIntl();
  const theme = useTheme();
  const breakpoints = useMinWidthTailwindBreakpoints();

  // Theme-aware colors
  const plotBorderColor = theme === "dark" ? colors.slate[800] : colors.slate[100];
  const legendColor = theme === "dark" ? colors.slate[300] : colors.slate[800];
  const dataLabelColor = theme === "dark" ? colors.slate[300] : colors.slate[800];
  const tooltipBgColor = theme === "dark" ? colors.slate[800] : colors.white;
  const tooltipBorderColor = theme === "dark" ? colors.slate[950] : colors.slate[200];
  const legendItemHoverColor = theme === "dark" ? colors.slate[400] : colors.slate[500];

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      type: "pie",
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 0,
      plotBorderColor,
    },
    scrollbar: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    legend: {
      align: "left",
      enabled: true,
      padding: 4,
      alignColumns: false,
      itemDistance: 16,
      layout: "horizontal",
      itemStyle: {
        color: legendColor,
        fontFamily: aeonik.style.fontFamily,
        fontWeight: "500",
        fontSize: "14px",
        textOverflow: "ellipsis",
        width: 240,
        textOutline: "none",
      },
      itemHoverStyle: {
        color: legendItemHoverColor,
      },
    },
    series: [
      {
        type: "pie",
        size: "70%",
        innerSize: "50%",
        borderRadius: 4,
        showInLegend: true,
        data: segments.map((node, index) => {
          const colorTheme = getColorThemeForIndex(index, theme as "light" | "dark");

          const { color, lineColor } = colorTheme;
          const isOtherSegment = node.name === "Others";

          return {
            name: !isOtherSegment
              ? node.name
              : intl.formatMessage({
                  defaultMessage: "Outros",
                  id: "8K4Qok",
                  description: "Complemento do gráfico de receitas",
                }),
            y: node.value,
            color,
            borderColor: lineColor,
            borderWidth: 1.5,
            custom: node,
            dataLabels: {
              enabled: breakpoints.lg || breakpoints.xl,
              style: {
                color: dataLabelColor,
                fontFamily: aeonik.style.fontFamily,
                fontWeight: "500",
                fontSize: "13px",
                textOutline: "none",
                textOverflow: "ellipsis",
                width: 240,
              },
            },
          };
        }),
      },
    ],
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: tooltipBgColor,
      borderColor: tooltipBorderColor,
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const point = (this.point as any).custom;
        return renderToStaticMarkup(
          <SegmentsPieChartTooltip
            currency={currency}
            segment={point.name}
            percentage={this.percentage}
            financialValue={point.financialValue}
          />,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        events: {
          legendItemClick: (event) => {
            event.preventDefault();

            return false;
          },
        },
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            callback: () => {
              return breakpoints.lg || breakpoints.xl;
            },
          },
          chartOptions: {
            legend: {
              enabled: false,
            },
          },
        },
      ],
    },
  };
  return <HighchartsChart options={options} className={className} />;
}
